import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Video, MapPin, Clock, Users, Link as LinkIcon, ChevronLeft, ChevronRight,
  CalendarDays, Phone, FileText, ListChecks, CheckSquare, Sparkles, Download,
  GraduationCap, UserCheck, Briefcase, Award,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import {
  PageHeader, KPICard, KPIGrid, FilterBar, StatusBadge, SectionCard, PillTabs, EmptyState, AvatarStack,
} from "@/components/app/PageKit";

const initials = (n = "U") => n.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");

// Meeting type → color tag
const MTG_TYPES = {
  one_on_one:    { label: "1:1",          dot: "bg-blue-500",    chip: "bg-blue-50 text-blue-700"    },
  team:          { label: "Team",         dot: "bg-violet-500",  chip: "bg-violet-50 text-violet-700" },
  hr:            { label: "HR",           dot: "bg-rose-500",    chip: "bg-rose-50 text-rose-700"    },
  interview:     { label: "Interview",    dot: "bg-amber-500",   chip: "bg-amber-50 text-amber-700"  },
  performance:   { label: "Performance",  dot: "bg-fuchsia-500", chip: "bg-fuchsia-50 text-fuchsia-700" },
  exit:          { label: "Exit",         dot: "bg-orange-500",  chip: "bg-orange-50 text-orange-700" },
  training:      { label: "Training",     dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700" },
  client:        { label: "Client",       dot: "bg-cyan-500",    chip: "bg-cyan-50 text-cyan-700"    },
};

const fmtDateTime = (iso) => {
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};
const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }); }
  catch { return ""; }
};
const meetingType = (m) => (m.type || (/(interview|hr screen)/i.test(m.title || "") ? "interview" : /1[:\s]*?1|one[-\s]?on[-\s]?one/i.test(m.title || "") ? "one_on_one" : "team"));

export default function Meetings() {
  const { user } = useAuth();
  const loc = useLocation();
  const [meetings, setMeetings] = useState([]);
  const [emps, setEmps] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("month"); // day | week | month | agenda
  const [cursor, setCursor] = useState(new Date());
  const [selected, setSelected] = useState(null);
  const nowStr = () => { const d = new Date(); d.setMinutes(d.getMinutes() + 30 - d.getMinutes() % 30); return d.toISOString().slice(0, 16); };
  const emptyForm = () => ({ title: "", description: "", start: nowStr(), end: "", location: "Online", meeting_link: "", attendees: [], agenda: "" });
  const [form, setForm] = useState(emptyForm());

  const load = async () => {
    try {
      const [m, e] = await Promise.all([api.get("/meetings"), api.get("/employees")]);
      setMeetings(m.data || []); setEmps(e.data || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (loc.state?.openAdd) setOpen(true); }, [loc.state]);

  const create = async () => {
    try {
      const start = new Date(form.start).toISOString();
      const end = form.end ? new Date(form.end).toISOString() : new Date(new Date(form.start).getTime() + 30 * 60000).toISOString();
      await api.post("/meetings", { ...form, start, end });
      toast.success("Meeting scheduled");
      setOpen(false); setForm(emptyForm()); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const today = new Date();
  const todayKey = today.toISOString().slice(0, 10);

  // KPI
  const todaysMeetings = meetings.filter(m => (m.start_time || "").startsWith(todayKey));
  const upcomingWeek = meetings.filter(m => {
    const t = new Date(m.start_time || m.start);
    return t >= today && t <= new Date(today.getTime() + 7 * 86400000);
  });
  const interviews = meetings.filter(m => meetingType(m) === "interview");

  // Month grid
  const ym = { y: cursor.getFullYear(), m: cursor.getMonth() };
  const monthName = cursor.toLocaleString("en", { month: "long" });
  const daysInMonth = new Date(ym.y, ym.m + 1, 0).getDate();
  const firstDay = new Date(ym.y, ym.m, 1).getDay();

  const meetingsByDay = useMemo(() => {
    const map = {};
    meetings.forEach((m) => {
      const k = (m.start_time || m.start || "").slice(0, 10);
      if (!map[k]) map[k] = [];
      map[k].push(m);
    });
    return map;
  }, [meetings]);

  const prevMonth = () => setCursor(new Date(ym.y, ym.m - 1, 1));
  const nextMonth = () => setCursor(new Date(ym.y, ym.m + 1, 1));

  // Selected meeting
  const selectedMeeting = selected ? meetings.find(m => m.id === selected) : null;
  const selectedAttendees = (selectedMeeting?.attendees || []).map(id => emps.find(e => e.id === id)).filter(Boolean);

  // Right rail: today's schedule
  const todayList = todaysMeetings.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")).slice(0, 5);
  const upcomingList = upcomingWeek.filter(m => !todaysMeetings.includes(m)).slice(0, 5);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 min-w-0" data-testid="meetings-page">
      <PageHeader
        category="WORK & COLLAB"
        title="Meetings & Calendar"
        description="Plan meetings, reviews, interviews, holidays, and team schedules — one premium hub."
        primaryAction={{ label: "Schedule Meeting", icon: Plus, onClick: () => setOpen(true), testid: "mtg-schedule" }}
        secondaryAction={{ label: "Export", icon: Download, onClick: () => toast.info("Export calendar (.ics)") }}
        moreActions={[
          { label: "Create Event",      icon: CalendarDays, onClick: () => toast.info("Create event") },
          { label: "Connect Google Cal", icon: LinkIcon,    onClick: () => toast.info("Connect Google") },
        ]}
      />

      {/* KPIs */}
      <KPIGrid cols={5}>
        <KPICard icon={CalendarDays} tone="violet"  label="Meetings Today"     value={todaysMeetings.length} sub={todaysMeetings.length ? "Don't be late" : "Free schedule"} testid="mtg-kpi-today" />
        <KPICard icon={Clock}        tone="blue"    label="Upcoming This Week" value={upcomingWeek.length}   sub="Next 7 days"                                              testid="mtg-kpi-week" />
        <KPICard icon={ListChecks}   tone="amber"   label="Pending Action Items" value={6}                   sub="Owed by you"                                              testid="mtg-kpi-actions" />
        <KPICard icon={UserCheck}    tone="fuchsia" label="Interviews Scheduled" value={interviews.length}   sub="In hiring pipeline"                                       testid="mtg-kpi-interviews" />
        <KPICard icon={Award}        tone="emerald" label="Holidays This Month"  value={2}                   sub="Calendar holidays"                                        testid="mtg-kpi-holidays" />
      </KPIGrid>

      {/* View tabs + filters */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <PillTabs
          value={view} onChange={setView}
          tabs={[
            { value: "day",     label: "Day"     },
            { value: "week",    label: "Week"    },
            { value: "month",   label: "Month"   },
            { value: "agenda",  label: "Agenda"  },
          ]}
          testid="mtg-view-tabs"
        />
        <FilterBar
          search={""} onSearch={() => {}}
          searchPlaceholder="Search meetings, attendees, agenda…"
          filters={[
            { label: "All Types",        onChange: () => {}, options: Object.entries(MTG_TYPES).map(([k, v]) => ({ value: k, label: v.label })) },
            { label: "All Departments",  onChange: () => {}, options: ["Engineering","HR","Sales","Design"] },
            { label: "All Locations",    onChange: () => {}, options: ["Bengaluru","Mumbai","Remote"] },
          ]}
        />
      </div>

      {/* Main + right rail */}
      <div className="grid xl:grid-cols-[1fr_320px] gap-5 min-w-0">
        <div className="min-w-0 space-y-5">
          {view === "month" && (
            <Card className="p-5 border-slate-200 shadow-sm bg-white" data-testid="mtg-month-view">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-bold font-[Outfit] tracking-tight text-slate-900">{monthName} {ym.y}</h2>
                  <button onClick={prevMonth} className="w-8 h-8 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-500"><ChevronLeft className="w-4 h-4" /></button>
                  <button onClick={nextMonth} className="w-8 h-8 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-500"><ChevronRight className="w-4 h-4" /></button>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {Object.entries(MTG_TYPES).slice(0, 4).map(([k, v]) => (
                    <span key={k} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600">
                      <span className={`w-1.5 h-1.5 rounded-full ${v.dot}`} />{v.label}
                    </span>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-7 gap-1.5 mb-2">
                {["SUN","MON","TUE","WED","THU","FRI","SAT"].map((d) => (
                  <div key={d} className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-400 text-center pb-1">{d}</div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1.5">
                {Array(firstDay).fill(0).map((_, i) => <div key={`pad-${i}`} className="aspect-[1.2/1]" />)}
                {Array(daysInMonth).fill(0).map((_, idx) => {
                  const d = idx + 1;
                  const iso = `${ym.y}-${String(ym.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                  const items = meetingsByDay[iso] || [];
                  const isToday = iso === todayKey;
                  return (
                    <div key={iso} className={`relative aspect-[1.2/1] rounded-lg border ${isToday ? "border-blue-400 ring-1 ring-blue-200 bg-blue-50/30" : "border-slate-100 bg-white hover:border-violet-200"} p-1.5 flex flex-col gap-0.5 transition`} data-testid={`mtg-day-${iso}`}>
                      <span className={`text-[12px] font-bold font-[Outfit] ${isToday ? "text-blue-700" : "text-slate-700"}`}>{d}</span>
                      {items.slice(0, 2).map((m) => {
                        const t = MTG_TYPES[meetingType(m)] || MTG_TYPES.team;
                        return (
                          <button key={m.id} onClick={() => setSelected(m.id)} className={`w-full text-left text-[10px] font-semibold px-1.5 py-0.5 rounded ${t.chip} truncate hover:opacity-80`}>
                            {fmtTime(m.start_time || m.start)} {m.title}
                          </button>
                        );
                      })}
                      {items.length > 2 && <span className="text-[9px] font-bold text-slate-500">+{items.length - 2} more</span>}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {view === "agenda" && (
            <SectionCard icon={CalendarDays} title="Agenda" sub={`${meetings.length} total meetings`}>
              {meetings.length === 0 ? (
                <EmptyState icon={CalendarDays} title="No meetings yet" message="Schedule your first meeting to start collaborating." action={{ label: "Schedule Meeting", icon: Plus, onClick: () => setOpen(true) }} />
              ) : (
                <div className="space-y-2">
                  {meetings.slice(0, 20).map((m) => {
                    const t = MTG_TYPES[meetingType(m)] || MTG_TYPES.team;
                    const att = (m.attendees || []).map(id => emps.find(e => e.id === id)).filter(Boolean);
                    return (
                      <div key={m.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:border-violet-200 hover:bg-violet-50/30 transition cursor-pointer" onClick={() => setSelected(m.id)}>
                        <span className={`w-1 h-10 rounded-full ${t.dot}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[13px] font-semibold text-slate-900 truncate">{m.title}</span>
                            <Badge className={`${t.chip} border-0 text-[10px] font-bold`}>{t.label}</Badge>
                          </div>
                          <div className="text-[11px] text-slate-500 truncate">{fmtDateTime(m.start_time || m.start)} · {m.location || "Online"}</div>
                        </div>
                        <AvatarStack people={att} max={3} />
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] h-8 shrink-0">
                          <Video className="w-3 h-3 mr-1" /> Join
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </SectionCard>
          )}

          {(view === "day" || view === "week") && (
            <SectionCard icon={Clock} title={view === "day" ? "Today's Schedule" : "This Week"} sub={view === "day" ? `${todayList.length} meeting(s)` : `${upcomingWeek.length} meeting(s)`}>
              <div className="space-y-2">
                {(view === "day" ? todayList : upcomingWeek).map((m) => {
                  const t = MTG_TYPES[meetingType(m)] || MTG_TYPES.team;
                  return (
                    <div key={m.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:bg-violet-50/30 transition cursor-pointer" onClick={() => setSelected(m.id)}>
                      <div className="w-14 text-center shrink-0">
                        <div className="text-[10px] uppercase font-bold text-slate-500">{new Date(m.start_time || m.start).toLocaleDateString([], { month: "short" })}</div>
                        <div className="text-xl font-bold font-[Outfit] text-slate-900 tabular-nums">{new Date(m.start_time || m.start).getDate()}</div>
                        <div className="text-[10px] text-slate-500">{fmtTime(m.start_time || m.start)}</div>
                      </div>
                      <span className={`w-1 h-10 rounded-full ${t.dot}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-slate-900 truncate">{m.title}</div>
                        <div className="text-[11px] text-slate-500 truncate">{m.location || "Online"} · {(m.attendees || []).length} attendees</div>
                      </div>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] h-8"><Video className="w-3 h-3 mr-1" /> Join</Button>
                    </div>
                  );
                })}
                {!(view === "day" ? todayList : upcomingWeek).length && (
                  <EmptyState icon={CalendarDays} title="Nothing scheduled" message="Your calendar is clear. Time for deep work!" />
                )}
              </div>
            </SectionCard>
          )}
        </div>

        {/* RIGHT RAIL */}
        <aside className="space-y-4 xl:sticky xl:top-20 xl:self-start">
          <SectionCard icon={Clock} title="Today's Schedule" sub={`${todayList.length} meeting(s)`}>
            {todayList.length === 0 ? (
              <div className="text-[12px] text-slate-400 text-center py-4">Free schedule today 🎉</div>
            ) : (
              <div className="space-y-3">
                {todayList.map((m) => {
                  const t = MTG_TYPES[meetingType(m)] || MTG_TYPES.team;
                  return (
                    <div key={m.id} className="flex items-center gap-2.5 cursor-pointer hover:bg-slate-50 -mx-2 px-2 py-1.5 rounded-lg transition" onClick={() => setSelected(m.id)}>
                      <span className={`w-1 h-9 rounded-full ${t.dot}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-semibold text-slate-900 truncate">{m.title}</div>
                        <div className="text-[10px] text-slate-500">{fmtTime(m.start_time || m.start)} · {m.location || "Online"}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </SectionCard>

          <SectionCard icon={ListChecks} title="Pending Action Items" sub="From recent meetings">
            <div className="space-y-2">
              {[
                { task: "Send Q2 OKRs deck to leadership", from: "Q2 Townhall",     due: "Today" },
                { task: "Schedule feedback call w/ Sneha", from: "1:1 · Sneha",     due: "Tomorrow" },
                { task: "Review hiring scorecard rubric",  from: "Recruiting Sync", due: "Fri" },
              ].map((a, i) => (
                <div key={i} className="flex items-start gap-2 p-2 rounded-lg hover:bg-slate-50">
                  <CheckSquare className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-semibold text-slate-700 leading-snug">{a.task}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{a.from} · <span className="text-amber-700 font-bold">{a.due}</span></div>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard icon={MapPin} title="Meeting Rooms" sub="Available now">
            <div className="space-y-2">
              {[
                { name: "Bengaluru — Aurora",   cap: 8,  free: true },
                { name: "Bengaluru — Nebula",   cap: 4,  free: true },
                { name: "Mumbai — Skyline",     cap: 12, free: false },
              ].map((r) => (
                <div key={r.name} className="flex items-center justify-between p-2 rounded-lg bg-slate-50">
                  <div className="min-w-0">
                    <div className="text-[12px] font-semibold text-slate-900 truncate">{r.name}</div>
                    <div className="text-[10px] text-slate-500">Capacity {r.cap}</div>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.free ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{r.free ? "Free" : "Busy"}</span>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard icon={Phone} title="Quick Actions" sub="Call & video shortcuts">
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" size="sm" className="border-slate-200" data-testid="mtg-quick-audio"><Phone className="w-3.5 h-3.5 mr-1" />Audio Call</Button>
              <Button variant="outline" size="sm" className="border-slate-200" data-testid="mtg-quick-video"><Video className="w-3.5 h-3.5 mr-1" />Video Call</Button>
              <Button variant="outline" size="sm" className="border-slate-200 col-span-2" data-testid="mtg-quick-meet"><LinkIcon className="w-3.5 h-3.5 mr-1" />Generate Meet Link</Button>
            </div>
          </SectionCard>
        </aside>
      </div>

      {/* DETAIL DRAWER (inline below) */}
      {selectedMeeting && (
        <Card className="p-5 border-slate-200 shadow-sm bg-white sticky bottom-4 z-20" data-testid="mtg-selected">
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Meeting Details</div>
              <h3 className="text-xl font-bold font-[Outfit] text-slate-900 truncate">{selectedMeeting.title}</h3>
              <div className="text-[12px] text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDateTime(selectedMeeting.start_time || selectedMeeting.start)}</span>
                {selectedMeeting.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{selectedMeeting.location}</span>}
                {selectedMeeting.meeting_link && <a href={selectedMeeting.meeting_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-violet-700 font-semibold"><LinkIcon className="w-3 h-3" />Join Link</a>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"><Video className="w-3.5 h-3.5 mr-1" />Join Meeting</Button>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700 px-2">✕</button>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Participants</div>
              <AvatarStack people={selectedAttendees} max={6} size={8} />
              <div className="text-[11px] text-slate-500 mt-2">{selectedAttendees.length} attendees</div>
            </div>
            <div className="md:col-span-2">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Agenda</div>
              <p className="text-[12px] text-slate-700">{selectedMeeting.agenda || selectedMeeting.description || "No agenda set yet."}</p>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <Button size="sm" variant="outline" className="border-slate-200 text-[11px]"><FileText className="w-3 h-3 mr-1" />Notes</Button>
                <Button size="sm" variant="outline" className="border-slate-200 text-[11px]"><ListChecks className="w-3 h-3 mr-1" />Action Items</Button>
                <Button size="sm" variant="outline" className="border-violet-200 text-violet-700 hover:bg-violet-50 text-[11px]"><Sparkles className="w-3 h-3 mr-1" />AI Summary</Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* SCHEDULE DIALOG */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-[Outfit]">Schedule a Meeting</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Q2 Roadmap Review" className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start</Label><Input type="datetime-local" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="mt-1" /></div>
              <div><Label>End</Label><Input type="datetime-local" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Online / Aurora room" className="mt-1" /></div>
            <div><Label>Meeting Link</Label><Input value={form.meeting_link} onChange={(e) => setForm({ ...form, meeting_link: e.target.value })} placeholder="https://meet.google.com/…" className="mt-1" /></div>
            <div><Label>Agenda</Label><Textarea value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })} rows={3} className="mt-1" placeholder="Discussion points…" /></div>
          </div>
          <DialogFooter><Button onClick={create} className="qh-grad-bg text-white">Schedule</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
